import { ApiEndpoints } from "./apiEndpoints";
import { fetchData, setHeaderOptions } from "./apiHelpers";
import {
  AccessChange,
  ContactWithReciprocal,
  CreateInvite,
  CreateRelationshipRow,
  RelationshipRow,
  UpdateRelationshipRow,
} from "./relationship.type";
export const getContacts = async (): Promise<RelationshipRow[]> => {
  try {
    const slug = ApiEndpoints.user_relationships + "/";

    const options = await setHeaderOptions({
      method: "GET",
    });
    const contacts = await fetchData<RelationshipRow[]>(slug, options);

    return contacts;
  } catch (err) {
    console.error("Error getting contacts:", err);
    throw err;
  }
};

export const getContactById = async (
  id: string
): Promise<ContactWithReciprocal> => {
  try {
    const slug = ApiEndpoints.user_relationships + `/${id}`;

    const options = await setHeaderOptions({
      method: "GET",
    });
    const contact = await fetchData<ContactWithReciprocal>(slug, options);

    return contact;
  } catch (err) {
    console.error(`Error getting contact with id ${id}:`, err);
    throw err;
  }
};

export const getContactsRelationships = async (
  relationshipId: string
): Promise<Partial<RelationshipRow>[]> => {
  const slug =
    ApiEndpoints.user_relationships + `/relationships/${relationshipId}`;
  const options = await setHeaderOptions({ method: "GET" });
  const relationships = await fetchData<Partial<RelationshipRow>[]>(
    slug,
    options
  );

  return relationships;
};

export const updateContact = async (
  contact: UpdateRelationshipRow
): Promise<RelationshipRow> => {
  try {
    // This is the relationship id not the user id
    const slug = ApiEndpoints.user_relationships + `/${contact.id}`;

    const options = await setHeaderOptions({
      method: "PUT",
      body: contact,
    });
    const updatedContact = await fetchData<RelationshipRow>(slug, options);

    return updatedContact;
  } catch (err) {
    console.error("Error updating contact:", err);
    throw err;
  }
};

export const addContactRelation = async ({
  contactUserId,
  relatedUserId,
  relatedUserRole,
}: {
  contactUserId: string;
  relatedUserId: string;
  relatedUserRole: string;
}): Promise<RelationshipRow> => {
  try {
    // This is the relationship id not the user id
    const slug =
      ApiEndpoints.user_relationships + `/${contactUserId}/related-user`;

    const options = await setHeaderOptions({
      method: "POST",
      body: {
        relatedUserId,
        relatedUserRole,
      },
    });
    const updatedContact = await fetchData<RelationshipRow>(slug, options);

    return updatedContact;
  } catch (err) {
    console.error("Error updating contact:", err);
    throw err;
  }
};

export const createContact = async (
  contact: CreateRelationshipRow
): Promise<RelationshipRow> => {
  try {
    const slug = ApiEndpoints.user_relationships + "/";

    // Make sure we have required fields
    const options = await setHeaderOptions({
      method: "POST",
      body: contact,
    });

    const createdContact = await fetchData<RelationshipRow>(slug, options);

    return createdContact;
  } catch (error) {
    console.error("Error creating contact:", error);
    console.error(
      "Error details:",
      error instanceof Error ? error.message : "Unknown error"
    );
    throw error;
  }
};

export const deleteContact = async (id: string): Promise<void> => {
  try {
    const slug = ApiEndpoints.user_relationships + `/${id}`;

    const options = await setHeaderOptions({
      method: "DELETE",
    });
    await fetchData<void>(slug, options);
  } catch (error) {
    console.error("Error deleting contact:", error);
    throw error;
  }
};

export const inviteContact = async (invite: CreateInvite): Promise<string> => {
  try {
    const slug = ApiEndpoints.user_relationships + `/invite`;

    const options = await setHeaderOptions({
      method: "POST",
      body: invite,
    });
    return await fetchData<string>(slug, options);
  } catch (error) {
    console.error("Error inviting contact:", error);
    throw error;
  }
};

export const handleRequestAccessChange = async ({
  relationshipId,
  accessType,
  accessMode,
  actionType,
}: {
  relationshipId: string;
  accessType: AccessChange["accessType"];
  accessMode: AccessChange["accessMode"];
  actionType: AccessChange["actionType"];
}): Promise<void> => {
  const slug =
    ApiEndpoints.user_relationships + `/${relationshipId}/access-change`;
  const options = await setHeaderOptions({
    method: "POST",
    body: { accessType, accessMode, actionType },
  });
  await fetchData<void>(slug, options);
};
